import { StarIcon } from '@heroicons/react/16/solid'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton from 'react-loading-skeleton'
import Button from '../../../components/tailwind/Button'
import trackEvents from '../../../constants/track-events'
import { trackEvent } from '../../../helpers/analytics'
import { useTalentsQuery } from '../../../hooks/use-talents-query'
import { useMatchBatchIfYouCanQuery } from '../../../hooks/useMatchBatchIfYouCanQuery'
import { useTalentStorage } from '../../pricing/hooks'
import { useDiscover } from '../hooks/useDiscover'
import { useSelectionParams } from '../hooks/useSelectionParams'
import {
  MESSAGE_ROLES,
  MESSAGE_TYPES,
  sendSmartMessageSequence
} from '../utils'
import {
  filterLockedSuggestions,
  pickSpaceIdForOwner
} from '../utils/talent-storage-lock'

const renderTalentsForCareer = (
  reachData,
  currentCareerId,
  t,
  dataTalents,
  hideAssistant,
  replaceTalent,
  talentStorage
) => {
  const res = JSON.parse(reachData)
  const matchingTalents = []

  Object.entries(res).forEach(([talentId, careerData]) => {
    // Check if this talent has data for the current career
    if (careerData[currentCareerId]) {
      const careerArray = careerData[currentCareerId]

      // Get the first (and likely only) career match object
      const careerMatch = careerArray[0]

      if (careerMatch) {
        matchingTalents.push({
          id: talentId,
          name: careerMatch.person_name || t('common.unknown_talent'),
          matchScore:
            careerMatch.matching_score_to_career || careerMatch.matching_score,
          talentId: careerMatch.user_id,
          category: 'default', // Adjust based on your needs
          careerData: careerMatch,
          onTalentClick: (talent) => {
            // Ohne Sperren der erste Space des Talents, sonst ein freier
            const spaceId = pickSpaceIdForOwner(
              dataTalents?.spaces,
              talent.talentId,
              talentStorage
            )

            trackEvent(
              trackEvents.DISCOVER_PRESSED_TALENT_MATCH_SUGGESTIONS_TALENT
            )
            hideAssistant()

            replaceTalent(spaceId)
          }
        })
      }
    }
  })

  return matchingTalents
}

export default function BetterMatchSuggestions({
  className,
  careerId,
  currentMatchingScore
}) {
  const { t } = useTranslation()
  const { replaceTalent } = useSelectionParams()
  const {
    setAssistantIsTyping,
    sendMessageToAssistant,
    enableAssistant,
    setAssistantDetail,
    hideAssistant
  } = useDiscover()
  const { data: dataTalents } = useTalentsQuery()
  const talentStorage = useTalentStorage({ fetch: 'auto' })
  const { data: matchData, isLoading } = useMatchBatchIfYouCanQuery({
    careers: [careerId],
    users: [],
    with_pre_matching: true,
    options: {
      select: (data) =>
        renderTalentsForCareer(
          data,
          careerId,
          t,

          dataTalents,
          hideAssistant,
          replaceTalent,
          talentStorage
        )
    }
  })

  // Gesperrte Talente schlägt Discover nicht vor, ihr Score stünde sonst im
  // Assistenten. Ohne Sperren dieselbe Referenz.
  const data = useMemo(
    () =>
      filterLockedSuggestions(matchData, {
        spaces: dataTalents?.spaces,
        storage: talentStorage
      }),
    [matchData, dataTalents, talentStorage]
  )

  if (isLoading) {
    return (
      <div className='mt-6 rounded border border-lightGrey px-4 py-3'>
        <Skeleton count={1} />
      </div>
    )
  }

  const onClick = () => {
    trackEvent(trackEvents.DISCOVER_PRESSED_BETTER_MATCH_SUGGESTIONS)
    enableAssistant()

    setAssistantDetail(true)
    sendSmartMessageSequence({
      setIsTyping: setAssistantIsTyping,
      messages: [
        {
          fn: () =>
            sendMessageToAssistant({
              id: 3,
              role: MESSAGE_ROLES.ASSISTANT,
              type: MESSAGE_TYPES.TALENT_MATCH_SUGGESTIONS,
              talentMatchSuggestionsProps: {
                title: t('discover.assistant.talent_match_suggestions.title'),
                description: t(
                  'discover.assistant.talent_match_suggestions.description'
                ),
                talents: data.filter((d) => d.matchScore > currentMatchingScore)
              }
            }),
          textLength:
            t('discover.assistant.talent_match_suggestions.title').length +
            t('discover.assistant.talent_match_suggestions.description').length
        }
      ]
    })
  }

  const hasBetterMatchesCount =
    data && data.length > 0
      ? data.filter((d) => d.matchScore > currentMatchingScore).length
      : 0

  if (hasBetterMatchesCount === 0) {
    return null
  }

  return (
    <div
      className={clsx(
        'flex items-center gap-1 text-sm text-darkBlack',
        className
      )}
    >
      <StarIcon className='size-4 text-buttonSecondaryBlue' />
      <span className='text-sm text-darkBlack'>
        {hasBetterMatchesCount === 1
          ? t('discover.betterMatchSuggestionsSection.description.singular', {
              count: hasBetterMatchesCount
            })
          : t('discover.betterMatchSuggestionsSection.description.plural', {
              count: hasBetterMatchesCount
            })}
        <Button.Text
          text={t('discover.betterMatchSuggestionsSection.cta')}
          onClick={onClick}
          className='ml-2'
        />
      </span>
    </div>
  )
}

BetterMatchSuggestions.propTypes = {
  className: PropTypes.string,
  careerId: PropTypes.string.isRequired,
  currentMatchingScore: PropTypes.number.isRequired
}
